import { getLogger } from '@trimble/logging';
import { SetStoreFunction } from 'solid-js/store';
import { isAbortError, OperationManager } from '@trimble/utilities';
import {
  IDataLoadingProvider,
  LoadableData,
  LoadableItem,
  LoadState,
} from './data-loading.types';
import { ProviderHandlerConfig } from './provider-handler.types';
import { isItemLoading, isItemLoadingOrLoaded } from './load-state-helpers';

/**
 * ProviderHandler handles loading/unloading items of a specific type into providers.
 *
 * Key responsibilities:
 * - Track which providers have loaded each item
 * - Load items into providers that haven't loaded them yet
 * - Handle cancellation via OperationManager
 * - Manage provider lifecycle (registration/unregistration)
 */
export class ProviderHandler<
  TProvider extends IDataLoadingProvider,
  TItem extends LoadableItem,
> {
  /** Track which providers have loaded each item */
  private readonly loadedProviders = new Map<
    string,
    Set<IDataLoadingProvider>
  >();

  private readonly operationManager = new OperationManager();

  constructor(
    private readonly config: ProviderHandlerConfig<TProvider, TItem>,
    private readonly dataState: LoadableData,
    private readonly setDataState: SetStoreFunction<LoadableData>
  ) {}

  public setLoadState(itemId: string, state: LoadState): void {
    this.config.setLoadState(this.setDataState, itemId, state);
  }

  public async handleProviderRegistration(
    provider: IDataLoadingProvider
  ): Promise<void> {
    if (!this.config.isProviderType(provider)) {
      return;
    }

    const loadableItems = this.config.getLoadableItems(this.dataState);
    if (
      loadableItems?.state !== LoadState.LoadComplete ||
      !loadableItems.items
    ) {
      return;
    }

    const loadPromises: Promise<void>[] = Object.values(loadableItems.items)
      .filter((item) => isItemLoadingOrLoaded(item.loadState))
      .map((item) => this.load(item, [provider]));

    await Promise.all(loadPromises);
  }

  /**
   * Cancel any in-progress operation for an item.
   */
  public cancelOperation(itemId: string): void {
    this.operationManager.cancel(itemId);
  }

  public cancelAllOperations(): void {
    this.operationManager.cancelAll();
  }

  public async load(
    item: TItem,
    providers: IDataLoadingProvider[]
  ): Promise<void> {
    await this.loadInternal(item, providers, false);
  }

  public async reload(
    item: TItem,
    providers: IDataLoadingProvider[]
  ): Promise<void> {
    // Cancel any in-progress operation
    this.operationManager.cancel(item.id);

    // Clear tracking so all providers reload
    this.clearItemTracking(item.id);

    // Load fresh with reload=true so providers know to unload first
    await this.loadInternal(item, providers, true);
  }

  private async loadInternal(
    item: TItem,
    providers: IDataLoadingProvider[],
    reload = false
  ): Promise<void> {
    this.setLoadState(item.id, LoadState.Loading);

    const matchingProviders = providers.filter(this.config.isProviderType);
    if (matchingProviders.length === 0) {
      // No matching providers - mark as complete immediately
      this.setLoadState(item.id, LoadState.LoadComplete);
      return;
    }

    const unloadedProviders = this.getNotLoadedProviders(
      item.id,
      matchingProviders
    );
    if (unloadedProviders.length === 0) {
      // All providers already have this item loaded
      this.setLoadState(item.id, LoadState.LoadComplete);
      return;
    }

    // Start operation and get signal for cancellation
    const signal = this.operationManager.start(item.id);

    try {
      const loadPromises = unloadedProviders.map(async (provider) => {
        this.markProviderLoading(item.id, provider);
        await this.config.loadItem(provider, item, { signal, reload });
      });

      await Promise.all(loadPromises);

      // Only update state if operation wasn't cancelled
      if (!signal.aborted) {
        this.setLoadState(item.id, LoadState.LoadComplete);
      }
    } catch (error) {
      if (!isAbortError(error) && !signal.aborted) {
        getLogger().error(`Loading failed for item: ${item.id}`, error);
        this.clearItemTracking(item.id);
        this.setLoadState(item.id, LoadState.Error);
        this.config.onLoadError?.(item.id);
      }
    } finally {
      // Mark operation as complete
      this.operationManager.complete(item.id);
    }
  }

  public async unload(
    item: TItem,
    providers: IDataLoadingProvider[]
  ): Promise<void> {
    // Cancel any in-progress load
    this.operationManager.cancel(item.id);

    // Clear tracking
    this.clearItemTracking(item.id);

    // Unload from all matching providers
    const matchingProviders = providers.filter(this.config.isProviderType);
    const unloadPromises = matchingProviders.map(async (provider) => {
      try {
        await this.config.unloadItem(provider, item);
      } catch (error) {
        getLogger().error(`Unload failed for item: ${item.id}`, error);
      }
    });

    await Promise.all(unloadPromises);

    await this.config.onItemFullyUnloaded?.(item);

    // Set not loaded state after unload completes
    this.setLoadState(item.id, LoadState.NotLoaded);
  }

  public async handleProviderUnregistration(
    provider: IDataLoadingProvider
  ): Promise<void> {
    if (!this.config.isProviderType(provider)) {
      return;
    }

    const loadableItems = this.config.getLoadableItems(this.dataState);
    if (
      loadableItems?.state !== LoadState.LoadComplete ||
      !loadableItems.items
    ) {
      return;
    }

    const typedProvider = provider;

    // build promises for every unload + follow-up update
    const unloadPromises: Promise<void>[] = Object.entries(
      loadableItems.items
    ).map(async ([key, item]) => {
      this.removeProviderFromItem(item.id, provider);
      const stillLoadedOnOtherProviders =
        (this.loadedProviders.get(item.id)?.size ?? 0) > 0;
      try {
        await this.config.unloadItem(typedProvider, item);
      } catch (error: unknown) {
        getLogger().error('Provider unloading failed during unregister', error);
      }

      if (!stillLoadedOnOtherProviders) {
        await this.config.onItemFullyUnloaded?.(item);
      }

      // If the item was in Loading state when provider was unregistered,
      // set it to LoadComplete to match expected behavior
      if (isItemLoading(item.loadState)) {
        this.setLoadState(key, LoadState.LoadComplete);
      }
    });

    // wait until every provider has finished unloading
    await Promise.all(unloadPromises);
  }

  private markProviderLoading(
    itemId: string,
    provider: IDataLoadingProvider
  ): void {
    let providers = this.loadedProviders.get(itemId);
    if (!providers) {
      providers = new Set();
      this.loadedProviders.set(itemId, providers);
    }
    providers.add(provider);
  }

  private getNotLoadedProviders(
    itemId: string,
    providers: TProvider[]
  ): TProvider[] {
    const loaded = this.loadedProviders.get(itemId);
    if (!loaded) {
      return providers;
    }
    return providers.filter((p) => !loaded.has(p));
  }

  private clearItemTracking(itemId: string): void {
    this.loadedProviders.delete(itemId);
  }

  private removeProviderFromItem(
    itemId: string,
    provider: IDataLoadingProvider
  ): void {
    const providers = this.loadedProviders.get(itemId);
    if (providers) {
      providers.delete(provider);
    }
  }
}
